import {
  calculateCloseQuantity,
  calculateProtectedLimitPrice,
} from "./funds_view";
import { findLeg as defaultFindLeg, MISSING_PRODUCT, positionKey } from "./position_model";
import { migrateExitsKeyMap } from "./hotkeys";

/**
 * Position Exit, Square-off Partitioning, Modal Builder & Exit Rule Evaluation.
 *
 * Implements portfolio margin-safe square-off partitioning (covering short legs
 * before selling hedge/long legs), filters cancellable resting broker orders,
 * generates exit watch modal HTML, validates levels, and checks exit triggers.
 */

export const CANCELLABLE_ORDER_STATUSES = [
  "OPEN",
  "PENDING",
  "TRIGGER_PENDING",
] as const;

export interface PositionRecord {
  tsym: string;
  exch?: string;
  netqty: string | number;
  prd?: string;
  lp?: string | number;
  [key: string]: any;
}

export interface OrderRecord {
  norenordno: string;
  status?: string;
  [key: string]: any;
}

export interface ExitRule {
  mode: "pts" | "pct";
  sl: number | null;
  tgt: number | null;
  trail: number | null;
  exch?: string;
  prd?: string;
  entry: number;
  best?: number | null;
  /** AUD-4: the signed net quantity of the position this rule was armed on.
   *  Absent on rules armed before AUD-4 (legacy) — those get the flat check only. */
  net_at_arm?: number | null;
}

/**
 * Partitions open positions into SHORT legs (covered first to release margin)
 * and LONG legs (hedges sold afterwards).
 */
export function partitionOpenPositions(
  openLegs: PositionRecord[] | null | undefined,
): { shorts: PositionRecord[]; longs: PositionRecord[] } {
  if (!Array.isArray(openLegs)) {
    return { shorts: [], longs: [] };
  }

  const shorts: PositionRecord[] = [];
  const longs: PositionRecord[] = [];

  for (const p of openLegs) {
    if (!p) continue;
    const qty = parseInt(String(p.netqty || 0), 10);
    if (qty < 0) shorts.push(p);
    else if (qty > 0) longs.push(p);
  }

  return { shorts, longs };
}

/**
 * Filters orders that are currently in an open or pending state at the broker.
 */
export function filterCancellableOrders(
  orders: OrderRecord[] | null | undefined,
): OrderRecord[] {
  if (!Array.isArray(orders)) return [];

  return orders.filter((o) => {
    if (!o) return false;
    const st = (o.status || "").toUpperCase();
    return CANCELLABLE_ORDER_STATUSES.includes(st as any);
  });
}

/**
 * Formats user-facing toast summary after portfolio square-off completion.
 */
export function formatSquareOffSummary(
  shortsCount: number,
  longsCount: number,
  totalCount: number,
): string {
  if (shortsCount > 0 && longsCount > 0) {
    return `✅ Squared off: ${shortsCount} short(s) covered + filled, then ${longsCount} hedge(s) sold.`;
  }
  return `Square off placed for ${totalCount} position(s).`;
}

/**
 * Builds the HTML markup for the live EXIT WATCH configuration modal.
 */
export function buildExitModalHtml(
  tsym: string,
  net: number,
  avg: number | string,
): string {
  return `<div class="modal-card ncm-card acc-orange" style="width:380px;">
      <div class="ncm-title">EXIT WATCH — ${tsym}</div>
      <div class="ncm-sub">entry avg ${avg} · net ${net} · triggers place a PROTECTED close with NO confirm · active only while this page is open</div>
      <div class="ncm-grid">
        <label>Mode<select id="x-mode"><option value="pts">Points</option><option value="pct">Percent %</option></select></label>
        <label>Stoploss<input id="x-sl" type="number" step="0.05" placeholder="e.g. 10"></label>
        <label>Target<input id="x-tgt" type="number" step="0.05" placeholder="blank = none"></label>
        <label>Trailing SL<input id="x-trail" type="number" step="0.05" placeholder="blank = fixed SL"></label>
      </div>
      <div class="ncm-foot spread">
        <button id="x-remove" class="ncm-btn ghost">Remove watch</button>
        <span class="right"><button id="x-cancel" class="ncm-btn ghost">Cancel</button>
        <button id="x-save" class="ncm-btn go">ARM</button></span>
      </div></div>`;
}

/**
 * Validates modal SL/TGT/Trail inputs against entry price and side, preventing impossible price levels.
 */
export function validateExitModalInputs(
  mode: "pts" | "pct",
  sl: number | null,
  tgt: number | null,
  trail: number | null,
  entry: number,
  net: number,
): { valid: boolean; error?: string } {
  if (!sl && !tgt && !trail) {
    return { valid: false, error: "Set at least one of SL / Target / Trail" };
  }

  if (entry > 0) {
    const long = net > 0;
    const toPts = (v: number) => (mode === "pct" ? (entry * v) / 100 : v);

    if (sl != null) {
      const lvl = long ? entry - toPts(sl) : entry + toPts(sl);
      if (lvl <= 0 || (long && lvl >= entry) || (!long && lvl <= entry)) {
        return {
          valid: false,
          error: `Impossible stoploss: level ${lvl.toFixed(2)} is on the wrong side of entry ${entry} (or ≤ 0)`,
        };
      }
    }

    if (tgt != null) {
      const lvl = long ? entry + toPts(tgt) : entry - toPts(tgt);
      if (lvl <= 0 || (long && lvl <= entry) || (!long && lvl >= entry)) {
        return {
          valid: false,
          error: `Impossible target: level ${lvl.toFixed(2)} is on the wrong side of entry ${entry} (or ≤ 0)`,
        };
      }
    }

    if (trail != null && toPts(trail) <= 0) {
      return {
        valid: false,
        error: "Trailing distance must be greater than 0",
      };
    }
  }

  return { valid: true };
}

/**
 * Evaluates whether current LTP triggers trailing stop, fixed SL, or Target for an active exit rule.
 */
export function evaluateExitHit(
  ex: ExitRule,
  net: number,
  lp: number,
): { hit: string | null; best: number | null } {
  if (!ex || !lp) return { hit: null, best: ex?.best ?? null };
  const long = net > 0;
  const toPts = (v: number) => (ex.mode === "pct" ? (ex.entry * v) / 100 : v);

  let best = ex.best ?? null;
  if (ex.trail != null) {
    best =
      best == null ? lp : long ? Math.max(best, lp) : Math.min(best, lp);
  }

  let hit: string | null = null;
  if (ex.trail != null && best != null) {
    const lvl = long ? best - toPts(ex.trail) : best + toPts(ex.trail);
    if ((long && lp <= lvl) || (!long && lp >= lvl)) {
      hit = `TRAIL (${lp} vs ${lvl.toFixed(2)})`;
    }
  }

  if (!hit && ex.sl != null) {
    const lvl = long ? ex.entry - toPts(ex.sl) : ex.entry + toPts(ex.sl);
    if ((long && lp <= lvl) || (!long && lp >= lvl)) {
      hit = `STOPLOSS (${lp} vs ${lvl.toFixed(2)})`;
    }
  }

  if (!hit && ex.tgt != null) {
    const lvl = long ? ex.entry + toPts(ex.tgt) : ex.entry - toPts(ex.tgt);
    if ((long && lp >= lvl) || (!long && lp <= lvl)) {
      hit = `TARGET (${lp} vs ${lvl.toFixed(2)})`;
    }
  }

  return { hit, best };
}

export interface ClosePositionOptions {
  tsym: string;
  exch: string;
  net: number;
  prd?: string;
  lp?: string | number | null;
  fraction?: number;
  auto?: boolean;
}

export interface ClosePositionDeps {
  isDebounced: (key: string) => boolean;
  getLotSize: () => number;
  getLiveLtp: (key: string) => number | null;
  getProtectionBand: () => number;
  getIndexSymbol: () => string;
  getFysym: (key: string) => string | null;
  submitOrder: (orderSpec: any) => Promise<any>;
  toast: (msg: string) => void;
  isRapid: () => boolean;
  newClientOrderId: () => string;
}

/**
 * Executes a protected market/limit close order for an open position.
 * Preserves exact execution logic, sizing, and debouncing rules from live.html.
 */
export async function executeClosePosition(
  options: ClosePositionOptions,
  deps: ClosePositionDeps,
): Promise<any> {
  const { tsym, exch, net, prd, lp, fraction = 1, auto = false } = options;
  const side: "BUY" | "SELL" = net > 0 ? "SELL" : "BUY";

  // Debounce a double-click on the SAME close button; never debounce a
  // programmatic close (evalExits / squareOff pass auto=true) — those aren't
  // user double-clicks and must always fire.
  if (!auto && deps.isDebounced("c:" + tsym + ":" + side + ":" + fraction)) {
    return;
  }

  // Partial closes round DOWN to a lot multiple (never oversell); lot
  // size inferred from the net qty's own divisors via the chain when
  // available, else 65/20 index defaults are avoided by using gcd-free
  // simple floor on raw qty — Flattrade rejects non-lot qtys loudly.
  const lot = deps.getLotSize() || 65;
  const { qty, error } = calculateCloseQuantity(net, fraction, lot);
  if (error) {
    deps.toast(error);
    return;
  }

  // MTM-1: never invent a product. A row the broker sent with no `prd` is
  // flagged at reconcile; closing it would have to GUESS which product to
  // square off, and guessing wrong opens a position instead of closing one.
  if (!prd || prd === MISSING_PRODUCT) {
    deps.toast(
      "Broker sent no product code for " +
        tsym +
        " — close it manually via the ticket",
    );
    return;
  }

  const pk = positionKey(tsym, prd);
  const liveLtp = deps.getLiveLtp(pk);
  const rawLtp = liveLtp != null ? liveLtp : lp;
  const ltp = parseFloat(String(rawLtp ?? ""));
  if (!ltp || isNaN(ltp)) {
    deps.toast("No LTP on this position row — close manually via the ticket");
    return;
  }

  const price = calculateProtectedLimitPrice(
    side,
    ltp,
    deps.getProtectionBand(),
  );

  // RETURN the submitOrder result so callers (squareOffAll) can await the fill
  // and read res.orders[].broker_order_id. Backward-compatible: the button
  // close, evalExits, and the exit modal ignore the return, unaffected.
  return await deps.submitOrder({
    side,
    qty,
    price,
    tsym,
    exch,
    product: prd,
    order_type: "LMT",
    trigger: null,
    symbol: deps.getIndexSymbol(),
    tick: 0.05,
    fysym: deps.getFysym(pk) || null,
    fillEstimate: ltp, // optimistic realized booked at the live LTP, not the protection limit
    clientOrderId: deps.newClientOrderId(), // one id per this intent (idempotent retry)
    skipConfirm: auto || deps.isRapid(),
    skipSyntheticCheck: true,
  });
}

export interface EvaluateExitRulesDeps {
  isServerFiring: () => boolean;
  findLeg?: (positions: any[], key: string) => any;
  onHit: (key: string, hit: string, rule: ExitRule) => void;
  /** AUD-4: the rule no longer belongs to a held position (the leg went flat, or
   *  its direction flipped) and was removed WITHOUT placing anything. Optional —
   *  when absent, onHit is called with a "REMOVED (…)" label for the same
   *  bookkeeping (persist + server remove). */
  onInvalidated?: (key: string, reason: "flat" | "reversed", rule: ExitRule) => void;
  closePosition: (
    tsym: string,
    exch: string,
    net: number,
    prd: string,
    lp: string,
    fraction: number,
    auto: boolean,
  ) => Promise<any> | void;
  toast: (msg: string) => void;
}

/**
 * AUD-4 — is this rule still bound to the position it was armed on?
 * Returns null when it is, else why not. A flat leg (Flattrade keeps closed legs
 * as netqty "0" rows) or a sign flip means any later position in the same
 * contract+product is a DIFFERENT position, whose entry the rule does not know.
 * Legacy rules without net_at_arm can only be checked for flat.
 */
export function exitRuleInvalidReason(
  ex: ExitRule,
  net: number,
): "flat" | "reversed" | null {
  if (!net) return "flat";
  const armed = Number(ex.net_at_arm);
  if (ex.net_at_arm != null && armed !== 0 && !isNaN(armed) && Math.sign(armed) !== Math.sign(net)) {
    return "reversed";
  }
  return null;
}

/**
 * Evaluates active exit watch rules against current streaming position prices.
 *
 * Implements the single-owner invariant: when isServerFiring() is true,
 * client-side evaluation completely halts to prevent dual-firing with the server.
 */
export function evaluateExitRules(
  exits: Record<string, ExitRule>,
  positions: any[],
  deps: EvaluateExitRulesDeps,
): void {
  // Single owner: when the server is firing exits itself, the client does NOT
  // evaluate or place — the server loop owns it (no double-close risk).
  if (deps.isServerFiring()) return;

  const legFinder = deps.findLeg || defaultFindLeg;

  for (const [key, ex] of Object.entries(exits)) {
    if (!ex) continue;
    // MTM-1: BY KEY. Matching on tsym alone took whichever product came first —
    // so an armed stop could evaluate against the wrong leg, or find a flat one
    // and never fire at all.
    const pos = legFinder(positions, key);
    if (!pos) continue;

    const tsym = pos.tsym;
    const net = parseInt(String(pos.netqty || 0), 10);

    // AUD-4: drop a rule whose position is gone or reversed BEFORE evaluating —
    // evaluating it would apply the old entry to a new position. Never places.
    const invalid = exitRuleInvalidReason(ex, isNaN(net) ? 0 : net);
    if (invalid) {
      delete exits[key];
      if (deps.onInvalidated) deps.onInvalidated(key, invalid, ex);
      else deps.onHit(key, `REMOVED (${invalid})`, ex);
      deps.toast(
        `⛨ Exit watch removed for ${tsym} — position ${invalid === "flat" ? "closed" : "reversed"}; nothing placed`,
      );
      continue;
    }

    const lp = parseFloat(String(pos.lp || 0));
    if (!lp || isNaN(lp)) continue;

    const evalRes = evaluateExitHit(ex, net, lp);
    ex.best = evalRes.best;
    const hit = evalRes.hit;

    if (hit) {
      // Only THIS product's rule. Keyed by symbol alone it removed a rule
      // the other leg still needed, leaving it open with no exit.
      delete exits[key];
      deps.onHit(key, hit, ex);
      deps.toast(`⛨ ${hit} hit on ${tsym} — placing protected close`);
      deps.closePosition(
        tsym,
        ex.exch || pos.exch || "NFO",
        net,
        ex.prd || pos.prd || "I",
        String(lp),
        1,
        true,
      );
    }
  }
}

/**
 * Formats badge text and visibility for the fixed exit-watch chip.
 */
export function formatExitChipText(count: number): { text: string; visible: boolean } {
  const n = Math.max(0, count || 0);
  if (n === 0) return { text: "", visible: false };
  return {
    text: `⛨ ${n} EXIT WATCH${n > 1 ? "ES" : ""} ACTIVE — page must stay open`,
    visible: true,
  };
}

/**
 * Loads and migrates local exit rules from storage.
 */
export function loadInitialExits(
  storage: { getItem: (k: string) => string | null },
  pkeyFn: (tsym: string, prd?: string) => string,
): Record<string, ExitRule> {
  try {
    const raw = JSON.parse(storage.getItem("nc_live_exits") || "{}");
    return migrateExitsKeyMap(raw, pkeyFn);
  } catch {
    return {};
  }
}

/**
 * Persists exit rules map to storage.
 */
export function persistExits(
  storage: { setItem: (k: string, v: string) => void },
  exits: Record<string, ExitRule>,
): void {
  storage.setItem("nc_live_exits", JSON.stringify(exits));
}

/**
 * Posts an armed exit rule to the server-side persistence store.
 */
export async function armServerExit(
  api: (url: string, init?: any) => Promise<any>,
  splitKey: (k: string) => { tsym: string; prd: string },
  getFysym: (k: string) => string | null,
  key: string,
  ex: ExitRule,
  net: any,
): Promise<void> {
  if (!ex) return;
  const { tsym } = splitKey(key);
  try {
    await api("/api/live-exits", {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({
        tsym,
        exch: ex.exch,
        prd: ex.prd,
        mode: ex.mode,
        sl: ex.sl,
        tgt: ex.tgt,
        trail: ex.trail,
        entry: ex.entry,
        net,
        fysym: getFysym(key) || null,
      }),
    });
  } catch {
    /* durability best-effort; client evalExits keeps working locally */
  }
}

/**
 * Deletes an armed exit rule from the server-side persistence store.
 */
export async function removeServerExit(
  api: (url: string, init?: any) => Promise<any>,
  splitKey: (k: string) => { tsym: string; prd: string },
  key: string,
): Promise<void> {
  const { tsym, prd } = splitKey(key);
  try {
    await api(
      "/api/live-exits/" +
        encodeURIComponent(tsym) +
        "?prd=" +
        encodeURIComponent(prd),
      { method: "DELETE" },
    );
  } catch {}
}

/**
 * Reconciles local exits with server state and broker-held positions.
 *
 * Implements zombie-exit prevention (2026-07-28):
 * - If server already has key -> keep server copy.
 * - If held at broker -> arm server and keep in working set.
 * - If confirmed FLAT at broker -> DROP to prevent resurrection.
 * - If broker held state is unknown (null) -> keep locally, skip arming server.
 */
export async function reconcileServerExits(
  localExits: Record<string, ExitRule>,
  serverExits: Record<string, ExitRule>,
  heldKeys: Set<string> | null,
  armFn: (key: string, ex: ExitRule, net: any) => Promise<void>,
): Promise<Record<string, ExitRule>> {
  const merged: Record<string, ExitRule> = { ...serverExits };

  for (const [key, ex] of Object.entries(localExits)) {
    if (merged[key]) continue;
    if (heldKeys && heldKeys.has(key)) {
      await armFn(key, ex, ex.net_at_arm);
      merged[key] = ex;
    } else if (heldKeys && !heldKeys.has(key)) {
      // confirmed FLAT -> drop, do NOT resurrect
    } else {
      // held unknown -> keep local, do NOT upload
      merged[key] = ex;
    }
  }

  return merged;
}

/**
 * Polls server exit state from /api/live-exits.
 */
export async function syncServerExitsState(
  api: (url: string) => Promise<any>,
  pkeyFn: (tsym: string, prd?: string) => string,
): Promise<{ enabled: boolean; firing: boolean; exits: Record<string, ExitRule> } | null> {
  try {
    const r = await api("/api/live-exits");
    if (!r.ok) return null;
    const body = await r.json();
    const mapped: Record<string, ExitRule> = {};
    for (const e of body.exits || []) {
      mapped[pkeyFn(e.tsym, e.prd)] = {
        mode: e.mode,
        sl: e.sl,
        tgt: e.tgt,
        trail: e.trail,
        exch: e.exch,
        prd: e.prd,
        entry: e.entry,
        best: e.best ?? null,
        net_at_arm: e.net_at_arm ?? null,   // AUD-4: keep the armed direction
      };
    }
    return {
      enabled: !!body.enabled,
      firing: !!body.firing,
      exits: mapped,
    };
  } catch {
    return null;
  }
}

export interface SquareOffDeps {
  confirm: (msg: string) => boolean;
  closePosition: (
    tsym: string,
    exch: string,
    net: number,
    prd?: string,
    lp?: string,
    fraction?: number,
    auto?: boolean,
  ) => Promise<any>;
  waitOrdersComplete: (ids: string[]) => Promise<{ ok: boolean; status?: string }>;
  toast: (msg: string) => void;
  refresh: () => void;
}

/**
 * Executes a portfolio-wide square-off with margin-safe sequencing.
 *
 * Line-by-line preserves the 2026-07-29 safety invariant:
 * 1. Closes shorts first (releases margin).
 * 2. Aborts hedge sales if any short order failed to place.
 * 3. Waits for short fills via waitOrdersComplete; aborts hedge sales if not filled.
 * 4. Sells long hedges only after short fills are confirmed.
 */
export async function executeSquareOffAll(
  open: PositionRecord[] | null | undefined,
  deps: SquareOffDeps,
): Promise<void> {
  const openLegs = open || [];
  if (!openLegs.length) {
    deps.toast("No open positions");
    return;
  }
  if (
    !deps.confirm(
      `SQUARE OFF ALL — ${openLegs.length} position(s) at protected market price?`,
    )
  ) {
    return;
  }

  const { shorts, longs } = partitionOpenPositions(openLegs);

  const shortIds: string[] = [];
  let allShortsPlaced = true;
  for (const p of shorts) {
    const res = await deps.closePosition(
      p.tsym,
      p.exch || "NFO",
      parseInt(String(p.netqty || 0), 10),
      p.prd,
      p.lp != null ? String(p.lp) : "",
      1,
      true,
    );
    const ids =
      res && Array.isArray(res.orders)
        ? res.orders.map((o: any) => o.broker_order_id).filter(Boolean)
        : [];
    if (!ids.length) allShortsPlaced = false;
    shortIds.push(...ids);
  }

  if (shorts.length && !allShortsPlaced) {
    deps.toast(
      "⛔ Not every short-cover order was placed — NOT selling the hedge(s). " +
        "Review positions; hedges left open on purpose.",
    );
    deps.refresh();
    return;
  }

  if (shortIds.length) {
    const done = await deps.waitOrdersComplete(shortIds);
    if (!done.ok) {
      deps.toast(
        `⛔ Short cover(s) not confirmed filled (${done.status || "timeout"}) — NOT selling the ` +
          "hedge(s) (would spike margin + reject). Review positions; hedges left open on purpose.",
      );
      deps.refresh();
      return;
    }
  }

  for (const p of longs) {
    await deps.closePosition(
      p.tsym,
      p.exch || "NFO",
      parseInt(String(p.netqty || 0), 10),
      p.prd,
      p.lp != null ? String(p.lp) : "",
      1,
      true,
    );
  }

  deps.toast(
    formatSquareOffSummary(shorts.length, longs.length, openLegs.length),
  );
}
